import { useState, useMemo, useEffect, useCallback, memo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import { assignmentService, documentService } from "@/services/documentService";
import { Document } from "@/types/document";
import { toast } from "@/hooks/use-toast";
import { axiosInstance, useAuth } from "@/pages/auth/auth.context";

const assignSchema = z.object({
  documentId: z.string().min(1, "Document is required"),
});

type AssignFormData = z.infer<typeof assignSchema>;

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  status: string;
  positionName?: string;
}

interface CreateAssignmentModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  preselectedDocument?: Document; // Optional: if provided, document selection will be pre-filled and disabled
}

const CreateAssignmentModal = memo(
  ({
    open,
    onClose,
    onSuccess,
    preselectedDocument,
  }: CreateAssignmentModalProps) => {
    const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
    const [userSearchTerm, setUserSearchTerm] = useState("");
    const { user } = useAuth();

    const form = useForm<AssignFormData>({
      resolver: zodResolver(assignSchema),
      defaultValues: {
        documentId: preselectedDocument?.id || "",
      },
    });

    // Reset form and state when modal is closed or preselectedDocument changes
    useEffect(() => {
      if (!open) {
        form.reset();
        setSelectedUsers(new Set());
        setUserSearchTerm("");
      } else if (preselectedDocument) {
        form.setValue("documentId", preselectedDocument.id);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, preselectedDocument]); // form is intentionally omitted to prevent infinite loop

    // Fetch documents for selection (only if no preselected document)
    const { data: documentsData, isLoading: loadingDocuments } = useQuery({
      queryKey: ["documents", "active"],
      queryFn: () =>
        documentService.getDocuments({ isActive: true, size: 100 }),
      enabled: open && !preselectedDocument, // Only fetch when modal is open and no preselected document
    });

    // Fetch users for selection
    const { data: users = [], isLoading: loadingUsers } = useQuery({
      queryKey: ["users", "active"],
      queryFn: async () => {
        const response = await axiosInstance.get("user?status=ACTIVE");
        return response.data.content || response.data;
      },
      enabled: open, // Only fetch when modal is open
    });

    const assignMutation = useMutation({
      mutationFn: async (data: AssignFormData) => {
        return assignmentService.createAssignment({
          documentId: data.documentId,
          userIds: Array.from(selectedUsers), // Use selectedUsers directly here
          assignmentType: "MANUAL",
          assignedBy: user?.id || "", // Use current user ID from auth context
        });
      },
      onSuccess: () => {
        toast({
          title: "Success",
          description: preselectedDocument
            ? `Document assigned to ${selectedUsers.size} user(s)`
            : `Assignment created for ${selectedUsers.size} user(s)`,
        });
        onSuccess();
        form.reset();
        setSelectedUsers(new Set());
      },
      onError: () => {
        toast({
          title: "Error",
          description: "Failed to create assignment",
          variant: "destructive",
        });
      },
    });

    const onSubmit = (data: AssignFormData) => {
      // Validate that users are selected (since we removed it from the schema)
      if (selectedUsers.size === 0) {
        toast({
          title: "Validation Error",
          description: "Please select at least one user",
          variant: "destructive",
        });
        return;
      }

      assignMutation.mutate(data); // Pass data directly, userIds is handled in mutation
    };

    const filteredUsers = useMemo(() => {
      if (!userSearchTerm.trim()) return users;

      const searchLower = userSearchTerm.toLowerCase();
      return users.filter((user: User) => {
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
        const email = user.email.toLowerCase();
        const position = user.positionName?.toLowerCase() || "";

        return (
          fullName.includes(searchLower) ||
          email.includes(searchLower) ||
          position.includes(searchLower)
        );
      });
    }, [users, userSearchTerm]);

    const toggleAllUsers = useCallback(() => {
      setSelectedUsers((prev) => {
        const allFilteredSelected = filteredUsers.every((user: User) =>
          prev.has(user.id),
        );

        if (allFilteredSelected && filteredUsers.length > 0) {
          // If all filtered users are selected, deselect them
          const newSet = new Set(prev);
          filteredUsers.forEach((user: User) => newSet.delete(user.id));
          return newSet;
        } else {
          // If not all filtered users are selected, select all filtered users
          const newSet = new Set(prev);
          filteredUsers.forEach((user: User) => newSet.add(user.id));
          return newSet;
        }
      });
    }, [filteredUsers]);

    const watchedDocumentId = form.watch("documentId");

    const selectedDocument = useMemo(() => {
      if (preselectedDocument) {
        return preselectedDocument;
      }

      // Otherwise, find the document from the documents list
      if (!watchedDocumentId || !documentsData?.content) return null;
      return documentsData.content.find(
        (doc: Document) => doc.id === watchedDocumentId,
      );
    }, [preselectedDocument, watchedDocumentId, documentsData?.content]);

    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {preselectedDocument ? "Assign Document" : "Create Assignment"}
            </DialogTitle>
            <DialogDescription>
              {preselectedDocument
                ? `Assign "${preselectedDocument.title}" to users who need to sign it.`
                : "Assign a document to users for signing."}
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {/* Document Selection - only show if no preselected document */}
              {!preselectedDocument && (
                <FormField
                  control={form.control}
                  name="documentId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Document</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a document" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {loadingDocuments ? (
                            <div className="p-4 text-center">
                              Loading documents...
                            </div>
                          ) : (
                            documentsData?.content.map((document: Document) => (
                              <SelectItem key={document.id} value={document.id}>
                                <div className="flex items-center gap-2">
                                  <FileText className="h-4 w-4" />
                                  <div>
                                    <div className="font-medium">
                                      {document.title}
                                    </div>
                                    <div className="text-sm text-muted-foreground">
                                      {document.documentType.replace("_", " ")}
                                    </div>
                                  </div>
                                </div>
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}{" "}
              {/* Selected Document Info */}
              {selectedDocument && (
                <div className="bg-muted p-3 rounded-lg">
                  <div className="text-sm font-medium">
                    {selectedDocument.title}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {selectedDocument.description && (
                      <div>{selectedDocument.description}</div>
                    )}
                    <div>
                      Type: {selectedDocument.documentType.replace("_", " ")} •
                      Size:{" "}
                      {(selectedDocument.fileSize / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                </div>
              )}
              {/* User Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Select Users
                </label>
                <div className="space-y-3">
                  {/* Search input */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search users by name, email, or position..."
                      value={userSearchTerm}
                      onChange={(e) => setUserSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>

                  {/* Selection controls */}
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                      {selectedUsers.size} of {users.length} user(s) selected
                      {userSearchTerm && ` • ${filteredUsers.length} shown`}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={toggleAllUsers}
                    >
                      {filteredUsers.length > 0 &&
                      filteredUsers.every((user: User) =>
                        selectedUsers.has(user.id),
                      )
                        ? "Deselect All"
                        : "Select All"}
                    </Button>
                  </div>

                  {/* User list */}
                  <div className="border rounded-lg max-h-64 overflow-y-auto">
                    {loadingUsers ? (
                      <div className="p-4 text-center text-muted-foreground">
                        Loading users...
                      </div>
                    ) : filteredUsers.length === 0 ? (
                      <div className="p-4 text-center text-muted-foreground">
                        {userSearchTerm
                          ? "No users found matching your search"
                          : "No active users found"}
                      </div>
                    ) : (
                      <div className="p-2 space-y-2">
                        {filteredUsers.map((user: User) => {
                          const isSelected = selectedUsers.has(user.id);
                          return (
                            <div
                              key={user.id}
                              className="flex items-center space-x-3 p-2 rounded hover:bg-muted"
                            >
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedUsers(
                                      (prev) => new Set([...prev, user.id]),
                                    );
                                  } else {
                                    setSelectedUsers((prev) => {
                                      const newSet = new Set(prev);
                                      newSet.delete(user.id);
                                      return newSet;
                                    });
                                  }
                                }}
                              />
                              <div
                                className="flex-1 cursor-pointer"
                                onClick={() => {
                                  const isCurrentlySelected = selectedUsers.has(
                                    user.id,
                                  );
                                  if (isCurrentlySelected) {
                                    setSelectedUsers((prev) => {
                                      const newSet = new Set(prev);
                                      newSet.delete(user.id);
                                      return newSet;
                                    });
                                  } else {
                                    setSelectedUsers(
                                      (prev) => new Set([...prev, user.id]),
                                    );
                                  }
                                }}
                              >
                                <div className="font-medium">
                                  {user.firstName} {user.lastName}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {user.email}
                                  {user.positionName &&
                                    ` • ${user.positionName}`}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={assignMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    selectedUsers.size === 0 ||
                    (!preselectedDocument && !watchedDocumentId) ||
                    assignMutation.isPending
                  }
                >
                  {assignMutation.isPending
                    ? preselectedDocument
                      ? "Assigning..."
                      : "Creating..."
                    : preselectedDocument
                      ? `Assign to ${selectedUsers.size} User(s)`
                      : `Create Assignment for ${selectedUsers.size} User(s)`}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    );
  },
);

export { CreateAssignmentModal };
